import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { filter, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { UserDetailsResponse } from '@core/models/company-api.models';
import { AppSwitcherService } from '@core/services/app-switcher.service';
import { AuthService } from '@core/services/auth.service';
import { CommandPaletteService, PaletteCommand } from '@core/services/command-palette.service';
import { CompanyApiService } from '@core/services/company-api.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { RuleConfigurationStatusService } from '@core/services/rule-configuration-status.service';
import { SalesChannelAccessService } from '@core/services/sales-channel-access.service';
import { StoreContextService } from '@core/services/store-context.service';
import { UserRolePermissionService } from '@core/services/user-role-permission.service';

import { IconComponent } from '@shared/components/icon/icon.component';
import { RequiredPrintFormatModalComponent } from '@shared/components/required-print-format-modal/required-print-format-modal.component';

import { AppLayoutComponent, NavSection } from './shared/components/app-layout/app-layout.component';

/**
 * Root component — toggles between the authenticated shell (AppLayoutComponent)
 * and a bare outlet for shell-less pages (e.g. 404).
 *
 * On first authenticated navigation: fetches the user's DefaultStoreId via the
 * Company API, then verifies the store accepts consignments via the Consignment
 * API. A full-screen loader covers the portal until validation resolves — the
 * portal renders only once the store is `'valid'`. If validation fails the
 * full-screen overlay is shown instead and clicking OK signs the user out.
 *
 * Polls the cross-app `logoutEvent` cookie every 3s so a sign-out triggered in
 * a sibling SSO app (Company, POS, IMS, etc.) propagates to this tab.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, AppLayoutComponent, MatButtonModule, IconComponent, RequiredPrintFormatModalComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly palette = inject(CommandPaletteService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly storeCtx = inject(StoreContextService);
  private readonly appSwitcher = inject(AppSwitcherService);
  private readonly channelAccess = inject(SalesChannelAccessService);
  private readonly userRolePermission = inject(UserRolePermissionService);
  private readonly ruleConfigStatus = inject(RuleConfigurationStatusService);

  readonly showShell = signal(false);
  /** Access gate, owned by StoreContextService and shared with the topbar store picker. */
  protected readonly storeStatus = this.storeCtx.storeStatus;
  private userDetailsLoaded = false;

  /**
   * True while an authenticated user's store is still being validated. The
   * portal stays hidden behind a full-screen loader until this is false; the
   * portal renders only once validation resolves to `'valid'`.
   */
  protected readonly isValidatingStore = computed(
    () => this.authService.isAuthenticated() && this.storeCtx.storeStatus() === 'unknown'
  );

  readonly sections: NavSection[] = [
    {
      title: '',
      items: [
        { label: 'Dashboard', icon: 'home', route: '/dashboard' },
        { label: 'Consignors', icon: 'users', route: '/consignors', permission: 'Consignor:View' },
        { label: 'Carts', icon: 'cart', route: '/carts', permission: 'Cart:View' },
        { label: 'Items', icon: 'package', route: '/items', permission: 'Item:View' },
        { label: 'Disputes', icon: 'alert-circle', route: '/disputes', permission: 'Dispute:View' },
        { label: 'Payouts', icon: 'dollar', route: '/payouts', permission: 'Overview:View' },
        { label: 'Tax & 1099', icon: 'file-text', route: '/tax', permission: 'Report:View' },
        { label: 'Rules', icon: 'sliders', route: '/rules', permission: 'Rule:View' },
        { label: 'Terms & Conditions', icon: 'list', route: '/terms', permission: 'TermsAndConditions:View' },
        { label: 'Settings', icon: 'settings', route: '/settings' }
      ]
    }
  ];

  ngOnInit(): void {
    // Clear any stale logout-broadcast cookie left by a prior session so the
    // 3s poll below doesn't immediately sign the freshly-authenticated user out.
    if (this.authService.isAuthenticated()) {
      this.authService.clearLogoutBroadcast();
    }

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        let r = this.route.snapshot;
        while (r.firstChild) r = r.firstChild;
        this.showShell.set(!r.data['hideShell']);
        this.loadUserDetailsAndValidateStore();
      });

    this.registerPaletteCommands();

    // Cross-app SSO logout broadcast — react to sibling-app sign-outs.
    const interval = setInterval(() => {
      if (this.authService.hasLogoutBroadcast()) {
        this.authService.clearLogoutBroadcast();
        this.currentUser.clear();
        this.storeCtx.reset();
        this.authService.clearAuthData();
        this.authService.redirectToLogin();
      }
    }, 3000);
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  /**
   * Sign-out navigates away via `window.location.href` (full page nav), so the
   * authenticated shell the user was on remains eligible for the browser's
   * back-forward cache. Clicking Back afterward can restore that page from
   * bfcache — showing stale authenticated UI without re-running route guards
   * or firing any Angular navigation events. Force a fresh load whenever a
   * bfcache restore lands on a now-unauthenticated session so the guard
   * re-evaluates and the user is redirected to login.
   */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (event.persisted && !this.authService.isAuthenticated()) {
      window.location.reload();
    }
  }

  onLogout(): void {
    this.currentUser.clear();
    this.storeCtx.reset();
    this.authService.performLogout();
  }

  private loadUserDetailsAndValidateStore(): void {
    if (this.userDetailsLoaded || !this.authService.isAuthenticated()) return;

    const userId = Number(this.currentUser.userId());
    if (!userId || isNaN(userId)) {
      // No resolvable user id → nothing to validate against; don't hang the loader.
      this.userDetailsLoaded = true;
      this.storeCtx.storeStatus.set('valid');
      return;
    }

    this.userDetailsLoaded = true;

    this.companyApi
      .getUserDetails(userId)
      .pipe(
        switchMap((res: UserDetailsResponse) => {
          const displayName = res.displayName?.trim() ?? '';
          if (displayName) this.currentUser.setApiDisplayName(displayName);
          this.currentUser.setApiProfileFileUrl(res.profileFileUrl?.trim() || null);

          this.appSwitcher.setApps(res.apps ?? []);
          this.storeCtx.setDefaultStoreId(res.defaultStoreId ?? null);
          this.loadSalesChannelAccess(res.companyId);
          this.loadPermissions(userId);
          this.loadRuleConfigurationStatus();

          // Load the consignment-accepting stores (for the picker), then resolve the
          // active store — persisted selection or the user's default — and validate it.
          return this.storeCtx.loadAcceptConsignmentStores().pipe(
            map(() => {
              this.storeCtx.initSelection();
              return null;
            }),
            catchError(() => {
              // Fail open: allow access when the store API is unreachable.
              this.storeCtx.storeStatus.set('valid');
              return of(null);
            })
          );
        }),
        catchError(() => {
          // Fail open: allow access when the Company API is unreachable.
          this.storeCtx.storeStatus.set('valid');
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Resolves the company's provisioned apps into the sales channels items may use.
   * Prefers the id from `Users/{id}`, falling back to the JWT claim.
   */
  private loadSalesChannelAccess(companyIdFromApi: number | null | undefined): void {
    const companyId = companyIdFromApi ?? Number(this.currentUser.companyId());
    if (!companyId || isNaN(companyId)) return;
    this.channelAccess.load(companyId).subscribe();
  }

  /** Fail open: an unreachable roles endpoint leaves ngx-permissions empty, not the app stuck loading. */
  private loadPermissions(userId: number): void {
    this.userRolePermission.fetchAndSetRoles(userId).subscribe({ error: () => undefined });
  }

  /** Gates Add Item / Accept / Reject on the result — see RuleConfigurationStatusService. */
  private loadRuleConfigurationStatus(): void {
    this.ruleConfigStatus.load().subscribe();
  }

  private registerPaletteCommands(): void {
    const navCommands: PaletteCommand[] = this.sections
      .flatMap(s => s.items)
      .map(item => ({
        id: `nav:${item.route}`,
        label: `Go to ${item.label}`,
        hint: item.route,
        icon: item.icon,
        group: 'Navigation',
        keywords: [item.label, item.route, 'go', 'open'],
        run: () => void this.router.navigate([item.route]),
        permission: item.permission
      }));

    const quickActions: PaletteCommand[] = [
      {
        id: 'action:new-consignor',
        label: 'Add Consignor',
        hint: 'Create a new consignor',
        icon: 'plus',
        group: 'Actions',
        keywords: ['new', 'create', 'add', 'consignor', 'vendor'],
        run: () => void this.router.navigate(['/consignors'], { queryParams: { create: 1 } }),
        permission: 'Consignor:Create'
      },
      {
        id: 'action:new-cart',
        label: 'Find / Scan Cart',
        hint: 'Open cart lookup',
        icon: 'scan',
        group: 'Actions',
        keywords: ['find', 'scan', 'lookup', 'cart'],
        run: () => void this.router.navigate(['/carts'], { queryParams: { lookup: 1 } }),
        permission: 'Cart:View'
      }
    ];

    this.palette.register([...navCommands, ...quickActions]);
  }
}
